import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Input } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

import useAuth from "../../hooks/useAuth";
import { UploadMedia } from "../../utils/mediaUpload";
import { isString, isValidInput } from "../../utils/validations";
import MainContainer from "../../layout/MainContainer";
import { CommonBody } from "../../types/General";
import { useUpdateProfileMutation } from "../../service/auth";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { errorToast, successToast } from "../../helpers";

const MyProfile = () => {
  const user = useAuth();
  const navigate = useNavigate();

  const [updateProfile] = useUpdateProfileMutation();
  const [error, setError] = useState(false);
  const [profileImage, setProfileImage] = useState<string>("");
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [middleName, setMiddleName] = useState(user?.middle_name || "");
  const [profile_picture_id, setProfile_picture_id] = useState();

  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone_no || "");
  const [dialCode, setDialCode] = useState(user?.country_code || "+971");

  const handleChangePhone = (phone: any, country: any) => {
    setPhone(phone?.replace(country.dialCode, ""));
    setDialCode(country?.dialCode);
  };

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage("");
      return;
    }
    if (
      file &&
      file.type &&
      file.type !== "image/jpeg" &&
      file.type !== "image/jpg" &&
      file.type !== "image/png"
    ) {
      errorToast("Please upload the image in the JPG, JPEG or PNG format");
      return;
    }
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.[0]?.media_url) {
      setProfileImage(imageUrl?.data?.[0]?.media_url);
      setProfile_picture_id(imageUrl?.data?.[0]?.id);
    }
  };

  const onSubmit = async () => {
    setError(true);
    if (!firstName?.length || firstName?.length <= 2) {
      return;
    }
    if (phone?.length <= 5) {
      return;
    }
    const body = {
      phone_no: phone,
      email,
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      profile_picture: Number(profile_picture_id),
      country_code: dialCode?.includes("+") ? dialCode : "+" + dialCode,
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await updateProfile(encryptedBody).unwrap();
      if (response?.status === 200) {
        successToast("Profile updated successfully");
        navigate("/dashboard");
      }
    } catch (error) {
      errorToast(error?.data?.message || "");
    }
  };

  useEffect(() => {
    if (user) {
      setFirstName(user?.first_name || "");
      setMiddleName(user?.middle_name || "");
      setLastName(user?.last_name || "");
      setEmail(user?.email || "");
      setPhone(user?.phone_no || "");
      setProfileImage(user?.profile_picture?.media_url || "");
      setDialCode(user?.country_code || "");
    }
  }, [user]);

  return (
    <MainContainer>
      <div className="main_title fdx">
        <div>
          <h1>My Profile</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> - My
            Profile
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_blue" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form">
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Profile Image</label>
              <div className="upload_box">
                <label>
                  {profileImage && (
                    <IconButton
                      className="crossIcon"
                      onClick={() => setProfileImage("")}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={
                        profileImage
                          ? profileImage
                          : "/static/images/user_placeholder.png"
                      }
                      alt="Upload"
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg,image/jpg",
                          }}
                          onChange={(e: any) => {
                            handleProfileUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
              </div>
            </div>
            <div className="control_group w_50">
              <label>First Name</label>
              <TextField
                variant="outlined"
                placeholder="First Name"
                fullWidth
                hiddenLabel
                value={firstName}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    setFirstName(val.target.value);
                  }
                }}
                inputProps={{ maxLength: 30 }}
              />
              {error && !firstName?.length ? (
                <h6 className="err_msg">This field is required</h6>
              ) : error && firstName?.length <= 2 ? (
                <h6 className="err_msg">
                  First name must be atleast 3 characters long
                </h6>
              ) : null}
            </div>
            <div className="control_group w_50">
              <label>Middle name</label>
              <TextField
                variant="outlined"
                placeholder="Middle name"
                fullWidth
                hiddenLabel
                value={middleName}
                inputProps={{ maxLength: 30 }}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    setMiddleName(val.target.value);
                  }
                }}
              />
            </div>
            <div className="control_group w_50">
              <label>Last name</label>
              <TextField
                variant="outlined"
                placeholder="Last name"
                fullWidth
                hiddenLabel
                value={lastName}
                inputProps={{ maxLength: 30 }}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    setLastName(val.target.value);
                  }
                }}
              />
            </div>

            <div className="control_group w_50">
              <label>E-mail</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                disabled
                hiddenLabel
                value={email}
                onChange={(val) => {
                  if (isValidInput(val.target.value)) {
                    setEmail(val.target.value);
                  }
                }}
              />
            </div>
            <div className="control_group w_50">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={dialCode + phone}
                country={"uae"}
                inputClass="phoneInput"
                onChange={(phone, country) => handleChangePhone(phone, country)}
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
              />
              {error && !phone?.length ? (
                <h6 className="err_msg">This field is required</h6>
              ) : error && phone?.length <= 5 ? (
                <h6 className="err_msg">
                  Phone number must contain atleast 6 digits
                </h6>
              ) : null}
            </div>
          </div>
          <div className="form_btn end">
            <Button className="btn btn_secondary" onClick={onSubmit}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default MyProfile;
